<template>
	<div class="text-list-sortable" ref="listContainer">
		<div class="list-header">
			<h3 class="section-title" :style="`color:${$appColors.primary}`">{{ title }}</h3>
			<button class="add-button" @click="addItem">
				<v-icon class="add-button-icon">add</v-icon>
			</button>
		</div>

		<ul :id="listId" class="list-content">
			<li
				v-for="(item, index) in items"
				:key="keyRender + item.id"
				class="list-item"
			>
				<p-drag-handle class="drag-handler"></p-drag-handle>
				<div class="item-number">{{ item.id }}</div>

				<div class="item-input-wrapper" @click="startEditing(index)">
					<v-text-field
						:value="editingIndex === index ? editingText : item.text"
						@input="onTextInput($event)"
						solo
						hide-details
						class="item-input"
						@keydown.enter="confirmItem"
						ref="textFields"
					></v-text-field>
				</div>

				<button
					v-show="editingIndex === index"
					class="action-button check-button"
					@click.stop="confirmItem"
				>
					<v-icon class="action-button-icon">check</v-icon>
				</button>

				<button
					class="action-button delete-button"
					@click="removeItem(index)"
				>
					<v-icon class="action-button-icon">close</v-icon>
				</button>
			</li>
		</ul>

		<p-empty-message v-if="items.length === 0">
			No hay elementos agregados
		</p-empty-message>

		<div class="list-footer">
			<p-button
				type="primary"
				size="small"
				:loading="saving"
				:disabled="items.length === 0 || !hasChanges"
				@click="saveItems"
			>
				{{ shouldShowUpdate ? 'Actualizar' : 'Crear' }}
			</p-button>
		</div>
	</div>
</template>

<script>
import { move } from 'ramda';
import Sortable from 'sortablejs';
import PButton from '@/modules/shared/components/ui/p-button';
import PEmptyMessage from '@/modules/shared/components/ui/p-empty-message';
import PDragHandle from '@/modules/shared/components/ui/p-drag-handler';
import { genId } from '@/modules/shared/utils/uuid';

function mounted() {
	this.$nextTick(() => {
		this.initSortable();
	});
	document.addEventListener('click', this.handleClickOutside);
}

function beforeDestroy() {
	document.removeEventListener('click', this.handleClickOutside);
	if (this.sortableInstance) {
		this.sortableInstance.destroy();
	}
}

function handleClickOutside(event) {
	if (this.editingIndex === null) return;

	const listContainer = this.$refs.listContainer;
	if (listContainer && !listContainer.contains(event.target)) {
		this.cancelEditing();
	}
}

function initSortable() {
	const table = document.getElementById(this.listId);
	if (!table) return;
	this.sortableInstance = new Sortable(table, {
		handle: '.drag-handler',
		animation: 150,
		onEnd: event => this.applySortChanges(event),
	});
}

function reinitSortable() {
	if (this.sortableInstance) {
		this.sortableInstance.destroy();
	}
	this.$nextTick(() => {
		this.initSortable();
	});
}

function applySortChanges(event) {
	const updatedItems = move(event.oldIndex, event.newIndex, this.items);
	const recalculatedItems = this.recalculateIds(updatedItems);
	this.$emit('update:items', recalculatedItems);
	this.markAsChanged();
}

function removeItem(index) {
	const newItems = this.items.filter((_, i) => i !== index);
	const updatedItems = this.recalculateIds(newItems);
	this.$emit('update:items', updatedItems);
	this.markAsChanged();
}

function addItem() {
	const newItems = [...this.items, { id: this.items.length + 1, text: '', persisted: false }];
	this.$emit('update:items', newItems);
}

function recalculateIds(items) {
	return items.map((item, index) => ({
		...item,
		id: index + 1,
	}));
}

function startEditing(index) {
	this.editingIndex = index;
	this.editingText = this.items[index].text;
	this.originalText = this.items[index].text;
}

function onTextInput(value) {
	this.editingText = value;
}

function confirmItem() {
	if (this.editingIndex === null) return;
	const newItems = [...this.items];
	newItems[this.editingIndex].text = this.editingText;
	if (!newItems[this.editingIndex].persisted) {
		newItems[this.editingIndex].persisted = true;
	}
	this.$emit('update:items', newItems);
	this.editingIndex = null;
	this.editingText = '';
	this.originalText = '';
	this.markAsChanged();
}

function cancelEditing() {
	if (this.editingIndex === null) return;
	const newItems = [...this.items];
	newItems[this.editingIndex].text = this.originalText;
	this.$emit('update:items', newItems);
	this.editingIndex = null;
	this.editingText = '';
	this.originalText = '';
}

function markAsChanged() {
	this.hasChanges = true;
}

async function saveItems() {
	try {
		this.saving = true;
		const validItems = this.items
			.filter(item => item.text && item.text.trim() !== '')
			.map(item => ({
				id: item.id,
				text: item.text,
			}));
		const sectionData = {
			type: this.sectionType,
			items: validItems,
			recordId: this.recordId,
		};
		this.$emit('save-section', sectionData);
		this.hasChanges = false;
		this.showNotification(this.shouldShowUpdate ? 'Sección actualizada correctamente' : 'Sección creada correctamente');
	} catch (error) {
		this.showGenericError();
	} finally {
		this.saving = false;
	}
}

function data() {
	return {
		listId: genId(),
		keyRender: 0,
		sortableInstance: null,
		editingIndex: null,
		editingText: '',
		originalText: '',
		hasChanges: false,
		saving: false,
	};
}

export default {
	name: 'text-list-sortable',
	components: {
		PButton,
		PEmptyMessage,
		PDragHandle,
	},
	props: {
		title: {
			type: String,
			default: 'Sección',
		},
		items: {
			type: Array,
			default: () => [],
		},
		sectionType: {
			type: String,
			default: '',
		},
		recordId: {
			type: [String, Number],
			default: null,
		},
	},
	data,
	mounted,
	beforeDestroy,
	computed: {
		shouldShowUpdate() {
			return this.recordId !== null && this.recordId !== undefined;
		},
	},
	methods: {
		initSortable,
		reinitSortable,
		applySortChanges,
		removeItem,
		addItem,
		recalculateIds,
		startEditing,
		onTextInput,
		confirmItem,
		cancelEditing,
		handleClickOutside,
		markAsChanged,
		saveItems,
	},
	watch: {
		items: {
			handler() {
				this.keyRender += 1;
			},
			deep: true,
		},
	},
};
</script>

<style lang="scss" scoped>
.text-list-sortable {
	background-color: white;
	border: 1px solid color(border-gray);
	border-radius: 8px;
	padding: 16px;
	margin-bottom: 20px;

	.list-header {
		display: flex;
		justify-content: space-between;
		align-items: center;
		margin-bottom: 16px;

		.section-title {
			font-size: 18px;
			font-family: font(bold);
			margin: 0;
		}

		.add-button {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 28px;
			height: 28px;
			border-radius: 50%;
			background-color: var(--app-color-primary);
			border: none;
			cursor: pointer;
			transition: all 0.2s ease;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);

			&:hover {
				background-color: var(--app-color-success);
				transform: translateY(-1px);
				box-shadow: 0 3px 6px rgba(0, 0, 0, 0.2);
			}

			&:active {
				transform: translateY(0);
			}

			::v-deep .v-icon {
				color: #ffffff !important;
				font-size: 18px;
			}
		}
	}

	.list-content {
		list-style: none;
		padding: 0;
		margin: 0;

		.list-item {
			display: flex;
			align-items: center;
			gap: 12px;
			padding: 8px 0;
			border-bottom: 1px solid color(border-gray);

			&:last-child {
				border-bottom: none;
			}

			.drag-handler {
				flex-shrink: 0;
				cursor: move;
			}

			.item-number {
				min-width: 30px;
				font-weight: bold;
				color: var(--app-color-primary);
				font-size: 14px;
			}

			.item-input-wrapper {
				flex: 1;
				cursor: text;
			}

			.item-input {
				flex: 1;

				::v-deep .v-input__slot {
					min-height: 37px !important;
				}

				::v-deep input {
					font-size: 14px;
				}
			}

			.action-button {
				display: flex;
				align-items: center;
				justify-content: center;
				width: 26px;
				height: 26px;
				border-radius: 50%;
				border: none;
				cursor: pointer;
				transition: all 0.2s ease;
				flex-shrink: 0;
				box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);

				::v-deep .v-icon {
					color: #ffffff !important;
					font-size: 16px;
				}

				&.check-button {
					background-color: #4caf50;

					&:hover {
						background-color: #43a047;
						transform: translateY(-1px);
						box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
					}

					&:active {
						transform: translateY(0);
					}
				}

				&.delete-button {
					background-color: #f44336;

					&:hover {
						background-color: #e53935;
						transform: translateY(-1px);
						box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
					}

					&:active {
						transform: translateY(0);
					}
				}
			}
		}
	}

	.list-footer {
		margin-top: 16px;
		display: flex;
		justify-content: flex-end;
	}
}
</style>

<style lang="scss">
.add-button .v-icon {
	color: #ffffff !important;
	font-size: 18px !important;
}

.action-button .v-icon {
	color: #ffffff !important;
	font-size: 16px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/configuration/development-plan/text-list-sortable.vue